import { apiGet, apiPost } from './client';

// ============================================================================
// Phase 8 — Calibration & score variance ("the eval you can trust").
//
// Runs every evaluation engine over a hand-labeled golden set and reports how
// well each engine's scores AGREE with the human labels, plus a repeat-run
// variance probe for model-based engines. JSON is camelCase.
//
// Engines in a CalibrationReport are pre-sorted best-agreement-first. An engine
// with ran=false carries an error string and zeroed metrics — render it muted
// with the reason. The variance endpoint returns 200 with a VarianceReport or
// 400 with a plain-string body (surfaced by apiPost as an Error message).
// ============================================================================

// ---- Golden set -------------------------------------------------------------

export interface GoldenChunk {
  documentName: string;
  pageNumber: number;
  content: string;
}

export interface GoldenLabel {
  id: string;
  question: string;
  answer: string;
  chunks: GoldenChunk[];
  citations: string[];
  humanGroundedness: number;   // 0..1
  humanRelevance: number;
  humanCitation: number;
  humanSafety: number;
  humanAggregate: number;
  note: string;
}

// ---- Calibration ------------------------------------------------------------

export interface DimAgreement {
  meanAbsoluteError: number;
  pearsonCorrelation: number | null;
  withinToleranceRate: number;   // 0..1 — the headline metric
}

export interface EngineAgreement {
  engineName: string;
  engineVersion: string;
  isModelBased: boolean;
  ran: boolean;
  error: string | null;
  sampleCount: number;
  groundedness: DimAgreement;
  relevance: DimAgreement;
  citation: DimAgreement;
  safety: DimAgreement;
  aggregate: DimAgreement;
}

export interface CalibrationReport {
  generatedAt: string;
  goldenSetSize: number;
  tolerance: number;
  bestByAgreement: string | null;
  engines: EngineAgreement[];    // pre-sorted best-agreement-first
}

export interface CalibrationRequest {
  engineNames?: string[];
  tolerance?: number;
}

// ---- Variance ---------------------------------------------------------------

export interface DimVariance {
  mean: number;
  stdDev: number;
  ci95Margin: number;
  samples: number[];
}

export interface VarianceReport {
  engineName: string;
  engineVersion: string;
  isModelBased: boolean;
  repeats: number;
  inputLabel: string;
  groundedness: DimVariance;
  relevance: DimVariance;
  citation: DimVariance;
  safety: DimVariance;
  aggregate: DimVariance;
}

export interface VarianceRequest {
  engineName?: string;                       // defaults "llm-judge" server-side
  goldenId?: string;                         // e.g. "gold-02"
  inlineInput?: Record<string, unknown>;
  repeats?: number;
}

// ============================================================================
// Client functions
// ============================================================================

/** GET /api/evaluations/calibration/golden — the hand-labeled transparency set. */
export const getGoldenSet = (): Promise<GoldenLabel[]> =>
  apiGet<GoldenLabel[]>('/api/evaluations/calibration/golden');

/** POST /api/evaluations/calibration — agreement report, best-first. */
export const runCalibration = (req: CalibrationRequest = {}): Promise<CalibrationReport> =>
  apiPost<CalibrationReport>('/api/evaluations/calibration', req);

/**
 * POST /api/evaluations/variance — repeat-run variance for a model-based engine.
 * 400s (as a thrown Error whose message carries the plain-string body) when the
 * engine is unavailable — callers should surface the message calmly.
 */
export const runVariance = (req: VarianceRequest): Promise<VarianceReport> =>
  apiPost<VarianceReport>('/api/evaluations/variance', req);
